import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './reports.css';

/**
 * V3 page shell for reports. Replaces the legacy reactstrap
 * Breadcrumbs + Container/Row/Col + Card wrapper with the V3 glass hero.
 *
 * Props:
 *   title     — report name (e.g. "Daywise Report")
 *   subtitle  — one-line description shown under the title
 *   crumb     — breadcrumb label above the title (default "Reports")
 *   actions   — node rendered in the hero's right-side action slot
 *               (typically <ReportExportButtons />)
 *   children  — page body (filter bar, table card, chart card, …)
 */
const ReportShell = ({ title, subtitle, crumb = 'Reports', crumbRoot = 'General settings', actions, children }) => {
    const { t } = useTranslation();
    // Repaint the legacy admin shell (body + page-body wrappers) with the V3
    // bg-base substrate while a report is open — same mechanism the /v3/*
    // routes use — so the translucent sidebar sits on the dark backdrop
    // instead of the grey V2 wrapper.
    useEffect(() => {
        document.body.classList.add('v3-themed');
        // `v3-report-open` lets reports.css drop the legacy fixed-header offset
        // (.page-body margin-top) and hide the unused V2 top header — the V3
        // sidebar already carries all navigation, so that band is dead space.
        document.body.classList.add('v3-report-open');
        return () => {
            document.body.classList.remove('v3-themed');
            document.body.classList.remove('v3-report-open');
        };
    }, []);

    return (
    <Fragment>
        <div className="v3-report-bg" aria-hidden="true" />
        <div className="v3-report-page">
            <div className="v3-report">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>
                            {t(crumbRoot)} › {t(crumb)}
                        </p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{title}</span>
                        </h1>
                        {subtitle && <p className="v3-body v3-sadmin-sub">{subtitle}</p>}
                    </div>
                    {actions && <div className="v3-sadmin-hero-actions">{actions}</div>}
                </div>
                {children}
            </div>
        </div>
    </Fragment>
    );
};

export default ReportShell;
